import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useRunner } from 'react-runner';
import * as framerMotion from 'framer-motion';
import * as LucideReact from 'lucide-react';
import { transform } from '@babel/standalone';
import { useTheme } from "../../context/ThemeProvider";
import { getColorSchemePalette } from "../../context/colorSchemes";
import useCopyToClipboard from "../../hooks/useCopyToClipboard";
import useDownloadFile from "../../hooks/useDownloadFile";
import VizCopyButton from "../shared/Viz/VizCopyButton";
import VizDownloadButton from "../shared/Viz/VizDownloadButton";
import VizActionBar from "../shared/Viz/VizActionBar";
import VizErrorBoundary from "../shared/Viz/VizErrorBoundary";
import useNativeFullscreen from "../../hooks/useNativeFullscreen";
import { useDiagramExport } from '../../hooks/useDiagramExport';
import logger from '../../services/Logger';
import { useOpenInNebula } from "../../hooks/useOpenInNebula";
import UserAuthStore from '../../store/userStore';

export const ReactVizCard = ({ children, ...props }) => <div {...props}>{children}</div>;

const ReactViz = ({ code, hideControls = false, toggleElement = null, onError }) => {
  const { theme, colorScheme } = useTheme();
  const palette = getColorSchemePalette(colorScheme, theme);
  const orgId = UserAuthStore(state => state.orgId);
  const openInNebula = useOpenInNebula();

  // ReactViz expects a complete source string. All mount sites (CodeBlock,
  // NebulaCanvas, NebulaOverview) gate on the per-message streaming flag and
  // only mount this component once the stream completes, so Babel never sees
  // a partial chunk. Do not reintroduce internal debouncing here — fix the
  // caller instead.
  const { copied, copyToClipboard } = useCopyToClipboard({ caller: "ReactViz" });
  const { downloadFile } = useDownloadFile({ caller: "ReactViz" });
  const containerRef = useRef(null);
  const { isFullscreen, enterFullscreen } = useNativeFullscreen(containerRef);

  const handleCopy = () => copyToClipboard(code);
  const handleDownload = () => downloadFile(code, `react-component-${Date.now()}.jsx`, "text/javascript");
  
  // Define the scope available to the dynamic code
  const scope = useMemo(() => ({
    React,
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
    // Expose framer-motion components and hooks
    motion: framerMotion.motion,
    AnimatePresence: framerMotion.AnimatePresence,
    useAnimation: framerMotion.useAnimation,
    useMotionValue: framerMotion.useMotionValue,
    useTransform: framerMotion.useTransform,
    useSpring: framerMotion.useSpring,
    // Expose lucide-react icons.
    // NOTE: spread FIRST so that native browser constructors below can override any
    // same-named Lucide icon component (e.g. LucideReact exports `Image`, `Link`,
    // `Audio`, etc.) that would otherwise shadow the global constructor and cause
    // "X is not a constructor" errors in generated code that calls `new Image()` etc.
    ...LucideReact,
    Card: ReactVizCard,
    // Expose theme context and a stable palette the LLM can use instead of
    // hardcoding dark-mode hex values (e.g. use THEME_COLORS.text instead of "#e2e8f0")
    theme,
    isDark: theme === 'dark',
    THEME_COLORS: {
      text:       palette.foreground,
      textMuted:  palette.mutedForeground,
      background: palette.background,
      surface:    palette.sidebar,
      border:     palette.border,
      grid:       palette.border,
      primary:    palette.primary,
      colorway:   theme === 'dark'
        ? ['#60C8F0', '#4F7AFF', '#A78BFA', '#34D399', '#FB8F8F', '#FBBF24', '#F472B6']
        : ['#3B6FF5', '#44B8D8', '#7C3AED', '#059669', '#E8624A', '#D97706', '#DB2777'],
    },
    // Standard Math functions are globally available, but we can be explicit if needed
    Math,
    // Infinity as a named export so generated code can write `repeat: Infinity` correctly.
    // Framer Motion 12 WAAPI path throws "iterationCount must be non-negative" when
    // repeat is -1 (CSS convention). Exposing `Infinity` makes the correct value
    // explicit and discoverable.
    Infinity,
    // ── Native browser constructors ──────────────────────────────────────────
    // Explicitly restore globals that lucide-react (or any future scope addition)
    // may shadow. These MUST come after the spread so they win.
    Image:                window.Image,
    Audio:                window.Audio,
    URL:                  window.URL,
    Worker:               window.Worker,
    Event:                window.Event,
    CustomEvent:          window.CustomEvent,
    WebSocket:            window.WebSocket,
    XMLHttpRequest:       window.XMLHttpRequest,
    ResizeObserver:       window.ResizeObserver,
    MutationObserver:     window.MutationObserver,
    IntersectionObserver: window.IntersectionObserver,
    FontFace:             window.FontFace,
    BroadcastChannel:     window.BroadcastChannel,
    // ── Media proxy ──────────────────────────────────────────────────────────
    // Use mediaSrc(assetId) as the src for generated media. Canonical asset IDs
    // are authorized by the backend; legacy storage paths remain supported.
    // Works for all media types: images, video, audio.
    mediaSrc: (assetRef) => assetRef?.startsWith('asset_')
      ? `/storage/assets/${encodeURIComponent(assetRef)}/content`
      : `/storage/download-media?file_path=${encodeURIComponent(assetRef)}&org_id=${encodeURIComponent(orgId)}`,
  }), [theme, palette, orgId]);

  // Pre-process code to remove imports which cause "Module not found" errors
  // and ensure it works with the runner which expects code body or export
  const processedCode = useMemo(() => {
    if (!code) return "";

    // Strip Google Fonts references before Babel sees the code.
    // The Babel ImportDeclaration visitor handles JS `import '...'` statements,
    // but CSS `@import url(...)` inside template literals and JSX `<link>` elements
    // are plain strings — they bypass AST transforms and get injected into the
    // document's stylesheets, where html-to-image then fetches every font variant.
    // Sora and Inter are already bundled locally via @fontsource so font-family
    // declarations still resolve correctly without the remote fetch.
    const stripped = code
      // CSS @import inside template literals: @import url('https://fonts.googleapis.com/...')
      .replace(/@import\s+url\(\s*['"]https:\/\/fonts\.googleapis\.com\/[^'"]*['"]\s*\)\s*;?/gi, '')
      // JSX <link href="https://fonts.googleapis.com/..." rel="stylesheet" ... />
      .replace(/<link[^>]*href=['"]https:\/\/fonts\.googleapis\.com\/[^'"]*['"][^>]*\/?>/gi, '');

    // ── Defensive media-path auto-wrap ───────────────────────────────────────
    // Generated code frequently embeds raw GCS object keys (e.g. stored in a
    // data array as `path: "org/users/<uid>/generated-images/.../img.png"`) and
    // then uses them directly as an <img>/<video>/<audio> src. A raw storage key
    // is NOT a fetchable URL, so the browser resolves it as a relative path and
    // 404s, producing blank media. The skill rules tell the model to wrap such
    // paths with mediaSrc(...), but it often forgets when the path lives in a
    // variable. Here we auto-route any bare generated-media key through
    // mediaSrc() so the proxy serves it regardless. The pattern is specific to
    // generated media keys (".../users/<id>/generated-(images|videos|audio)/...")
    // to avoid touching unrelated strings, and a negative lookbehind prevents
    // double-wrapping paths already passed to mediaSrc(...).
    // Extra guard: if the matched string is already a proxy URL (contains
    // "download-media" or "file_path="), skip it — calling mediaSrc() on a
    // proxy URL produces a broken double-wrapped URL like
    // /storage/download-media?file_path=%2Fstorage%2Fdownload-media%3F...
    const withMediaSrc = stripped.replace(
      /(?<!mediaSrc\(\s*)(['"])([^'"\s]+\/users\/[^'"\s]+\/generated-(?:images|videos|audio)\/[^'"\s]+)\1/g,
      (match, q, path) => {
        if (path.includes('download-media') || path.includes('file_path=')) return match;
        return `mediaSrc("${path}")`;
      }
    );

    try {
      // Use Babel to transform the code:
      // 1. Parse and remove import statements (since we provide scope)
      // 2. Compile JSX/ES6 to standard JS
      const result = transform(withMediaSrc, {
        presets: ['react', 'env'],
        plugins: [
          // Custom plugin to remove import statements
          {
            visitor: {
              ImportDeclaration(path) {
                path.remove();
              }
            }
          }
        ],
        filename: 'dynamic.js',
        // Required so the parser accepts top-level `import` statements
        // (otherwise it treats them as identifiers and throws
        // "Unexpected token, expected ';'" at column 7). The
        // ImportDeclaration visitor above then strips them.
        sourceType: 'module',
      });
      return result.code;
    } catch (err) {
      // If Babel fails, fall back to a regex import strip so the runner can
      // still surface the real error to the user instead of a blank canvas.
      logger.warn('ReactViz', 'Babel transform failed', { error: err.message });
      return withMediaSrc.replace(/^import\s+.*$/gm, '');
    }
  }, [code]);

  // Retry loop: if react-runner throws "Identifier X has already been declared",
  // it means the generated code declares a variable whose name is already in
  // scope (most commonly a Lucide icon name like `Chrome`, `Link`, `Image`).
  // We fix it by converting `const X =` / `let X =` / `var X =` into `X =`
  // (a bare reassignment), then re-run. Cap at MAX_DECL_RETRIES to prevent
  // infinite loops if something else is wrong.
  const MAX_DECL_RETRIES = 6;
  const [runCode, setRunCode] = useState(processedCode);
  const retryCountRef = useRef(0);

  useEffect(() => {
    retryCountRef.current = 0;
    setRunCode(processedCode);
  }, [processedCode]);

  const { element, error } = useRunner({
    code: runCode,
    scope,
    type: 'typescript'
  });

  useEffect(() => {
    if (!error) {
      onError?.(false);
      return;
    }
    const msg = error?.message || error?.toString() || '';
    const match = msg.match(/Identifier '(\w+)' has already been declared/);
    if (match && retryCountRef.current < MAX_DECL_RETRIES) {
      const name = match[1];
      const fixed = runCode.replace(
        new RegExp(`\\b(const|let|var)\\s+(${name})\\s*=`, 'g'),
        '$2 ='
      );
      if (fixed !== runCode) {
        retryCountRef.current += 1;
        setRunCode(fixed);
        return; // don't surface error yet — retry is in flight
      }
    }
    onError?.(true);
  }, [error, runCode, onError]);

  useDiagramExport(containerRef, code, { delay: 800, trigger: element, enabled: !!element && !error });

  // Suppress the error display while a retry is pending (runCode just changed
  // but useRunner hasn't re-evaluated yet). Only show an error after we've
  // exhausted retries or the error is not a duplicate-declaration one.
  const isDupliceDeclError = (error?.message || '').includes('has already been declared');
  const showError = !!error && (!isDupliceDeclError || retryCountRef.current >= MAX_DECL_RETRIES);

  if (showError) {
    return (
      <div className="p-4 bg-fill-red-50 dark:bg-fill-red-900/20 border border-edge-red-200 dark:border-edge-red-800 rounded-lg text-ink-red-600 dark:text-ink-red-400 text-sm font-mono">
        <p className="font-bold mb-1">Visualization Error</p>
        {error.toString()}
        <pre className="mt-2 text-xs opacity-75 overflow-x-auto">{code}</pre>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full my-4 rounded-xl bg-transparent font-sans relative group">
      {/* Action Buttons */}
      {!isFullscreen && !hideControls && (
        <VizActionBar onFullscreen={enterFullscreen} leftContent={toggleElement} onOpenInNebula={openInNebula}>
          <VizCopyButton copied={copied} onClick={handleCopy} tooltip="Copy code" />
          <VizDownloadButton onClick={handleDownload} tooltip="Download as .jsx" />
        </VizActionBar>
      )}
      <div className="w-full overflow-x-auto">
        {/* Aspect-ratio shell: ensures presentation-renderer slides (which use
            height:100% internally) have a real pixel height to fill. Without
            this, height:100% on child slides resolves to 0 and content
            collapses into whitespace. Non-presentation react-viz components
            are unaffected — they size themselves by content, not by height. */}
        <div style={{ position: "relative", width: "100%", paddingBottom: "56.25%" /* 16:9 */ }}>
          <div style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: 12 }}>
            <div className="w-full h-full whitespace-normal break-words">
              <VizErrorBoundary title="Runtime Error" caller="ReactViz">
                {element}
              </VizErrorBoundary>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ReactViz;
